import React, { useMemo } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Badge } from '../../../../catalyst-components/badge';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { computeDiff, DiffSegment } from '../../../../shared/utils/text-diff';

const ACTION_COLORS: Record<string, string> = {
    create: 'green',
    update: 'blue',
    delete: 'red',
    toggle: 'yellow',
    reorder: 'purple',
    seed: 'cyan',
    grant: 'orange',
};

function formatTimestamp(ts: number): string {
    return new Date(ts).toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

function formatEntityType(type: string): string {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function formatFullValue(val: unknown): string {
    if (val === null || val === undefined) return '(empty)';
    if (typeof val === 'string') return val;
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    if (typeof val === 'number') return String(val);
    return JSON.stringify(val, null, 2);
}

function isLongValue(val: unknown): boolean {
    const s = formatFullValue(val);
    return s.length > 120 || s.includes('\n');
}

const HighlightedSegments = ({ segments, mode }: { segments: DiffSegment[]; mode: 'removed' | 'added' }) => (
    <>
        {segments.map((seg, i) => {
            if (seg.type === 'equal') {
                return <span key={i}>{seg.text}</span>;
            }
            if (mode === 'removed' && seg.type === 'removed') {
                return <span key={i} className="bg-red-200 text-red-900 rounded-sm px-0.5 line-through decoration-red-400">{seg.text}</span>;
            }
            if (mode === 'added' && seg.type === 'added') {
                return <span key={i} className="bg-green-200 text-green-900 rounded-sm px-0.5">{seg.text}</span>;
            }
            return null;
        })}
    </>
);

const DiffRow = ({ field, from, to }: { field: string; from: unknown; to: unknown }) => {
    const fromStr = formatFullValue(from);
    const toStr = formatFullValue(to);
    const isLong = isLongValue(from) || isLongValue(to);

    const diff = useMemo(() => computeDiff(fromStr, toStr), [fromStr, toStr]);

    if (isLong) {
        return (
            <div className="border-b border-border/30 last:border-0 py-4">
                <div className="font-medium text-foreground mb-2">{field}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <div className="text-xs font-medium text-muted-foreground mb-1">Before</div>
                        <pre className="text-sm bg-muted/30 border border-border/50 rounded-lg p-3 whitespace-pre-wrap break-all text-foreground max-h-64 overflow-auto">
                            <HighlightedSegments segments={diff.removed} mode="removed" />
                        </pre>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-muted-foreground mb-1">After</div>
                        <pre className="text-sm bg-muted/30 border border-border/50 rounded-lg p-3 whitespace-pre-wrap break-all text-foreground max-h-64 overflow-auto">
                            <HighlightedSegments segments={diff.added} mode="added" />
                        </pre>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="border-b border-border/30 last:border-0 py-3 grid grid-cols-[200px_1fr_1fr] gap-4 items-start">
            <div className="font-medium text-foreground">{field}</div>
            <div className="text-sm bg-muted/30 border border-border/50 rounded px-3 py-1.5 text-foreground break-all">
                <HighlightedSegments segments={diff.removed} mode="removed" />
            </div>
            <div className="text-sm bg-muted/30 border border-border/50 rounded px-3 py-1.5 text-foreground break-all">
                <HighlightedSegments segments={diff.added} mode="added" />
            </div>
        </div>
    );
};

export const AuditLogDetail: React.FC = () => {
    const navigate = useNavigate();
    const { entryId } = useParams<{ entryId: string }>();

    const entry = useQuery(
        api.audit_log.getEntry,
        entryId ? { id: entryId as Id<"audit_log"> } : 'skip'
    );

    if (entry === undefined) {
        return (
            <div className="min-h-screen bg-background pt-24">
                <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/audit-log')}>
                                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                                Back to Audit Log
                            </Button>
                        </div>
                        <h1 className="text-3xl font-semibold text-foreground">Audit Log Entry</h1>
                    </div>
                </div>
                <div className="flex justify-center items-center h-64">
                    <Spinner className="h-12 w-12" />
                </div>
            </div>
        );
    }

    if (entry === null) {
        return (
            <div className="min-h-screen bg-background pt-24">
                <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/audit-log')}>
                                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                                Back to Audit Log
                            </Button>
                        </div>
                        <h1 className="text-3xl font-semibold text-foreground">Entry Not Found</h1>
                    </div>
                </div>
            </div>
        );
    }

    // Parse details
    let details: Record<string, unknown> | null = null;
    let isDiff = false;
    if (entry.details) {
        try {
            details = JSON.parse(entry.details);
            const entries = Object.entries(details!);
            isDiff = entries.some(([, v]) => v && typeof v === 'object' && 'from' in (v as any) && 'to' in (v as any));
        } catch {
            // not valid JSON
        }
    }

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/audit-log')}>
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back to Audit Log
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <Badge color={(ACTION_COLORS[entry.action] || 'zinc') as any} className="text-lg px-4 py-1">
                            {entry.action}
                        </Badge>
                        <div>
                            <h1 className="text-2xl font-semibold text-foreground">{entry.description}</h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {formatTimestamp(entry.timestamp)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-6">
                    {/* Metadata */}
                    <div className="border-2 border-border/50 rounded-xl bg-card p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Entry Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Admin</div>
                                <div className="text-sm text-foreground">{entry.admin_email}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Timestamp</div>
                                <div className="text-sm text-foreground">{formatTimestamp(entry.timestamp)}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Entity Type</div>
                                <div className="text-sm text-foreground">{formatEntityType(entry.entity_type)}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Entity ID</div>
                                <div className="text-sm text-foreground font-mono">{entry.entity_id || '—'}</div>
                            </div>
                            <div className="md:col-span-2">
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Description</div>
                                <div className="text-sm text-foreground">{entry.description}</div>
                            </div>
                        </div>
                    </div>

                    {/* Changes */}
                    {details && isDiff && (
                        <div className="border-2 border-border/50 rounded-xl bg-card p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <h2 className="text-lg font-semibold text-foreground">Changes</h2>
                                <span className="text-sm text-muted-foreground">
                                    {Object.keys(details).length} field{Object.keys(details).length !== 1 ? 's' : ''} modified
                                </span>
                            </div>
                            {/* Column headers for short rows */}
                            <div className="hidden md:grid grid-cols-[200px_1fr_1fr] gap-4 pb-2 border-b border-border/50 mb-2">
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Field</div>
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Before</div>
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">After</div>
                            </div>
                            {Object.entries(details).map(([key, val]) => {
                                const diff = val as { from: unknown; to: unknown };
                                return <DiffRow key={key} field={key} from={diff.from} to={diff.to} />;
                            })}
                        </div>
                    )}

                    {/* Raw details for non-diff format */}
                    {details && !isDiff && (
                        <div className="border-2 border-border/50 rounded-xl bg-card p-6">
                            <h2 className="text-lg font-semibold text-foreground mb-4">Details</h2>
                            <pre className="text-sm bg-muted/30 rounded-lg p-4 whitespace-pre-wrap break-all overflow-auto max-h-96">
                                {JSON.stringify(details, null, 2)}
                            </pre>
                        </div>
                    )}

                    {/* Raw string if not JSON */}
                    {entry.details && !details && (
                        <div className="border-2 border-border/50 rounded-xl bg-card p-6">
                            <h2 className="text-lg font-semibold text-foreground mb-4">Details</h2>
                            <pre className="text-sm bg-muted/30 rounded-lg p-4 whitespace-pre-wrap break-all overflow-auto max-h-96">
                                {entry.details}
                            </pre>
                        </div>
                    )}

                    {!entry.details && (
                        <div className="border-2 border-border/50 rounded-xl bg-card p-6 text-center text-muted-foreground">
                            No change details recorded for this entry.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
